/**
 * @format
 */
import { Inject, Injectable, isDevMode } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { NG_GOOGLE_ANALYTICS_SETTINGS_TOKEN } from "../tokens/google-analitics-settings-token";
import { GoogleAnalyticsSettings } from "../interfaces/google-analytics-settings";
import { NG_GTAG_FN } from "../tokens/gtag-token";
import { GtagFn } from "../types/gtag-token.type";
import { GaActionEnum } from "../enums/ga-action.enum";

@Injectable()
export class GoogleAnalyticsService {
   private get document(): Document {
      return this._document;
   }

   constructor(
      @Inject(NG_GOOGLE_ANALYTICS_SETTINGS_TOKEN) private readonly settings: GoogleAnalyticsSettings,
      @Inject(DOCUMENT) private readonly _document: any,
      @Inject(NG_GTAG_FN) private readonly _gtag: GtagFn
   ) {}

   private throw(err: Error) {
      if ((this.settings.enableTracing || isDevMode()) && console && console.error) {
         console.error(err);
      }
   }

   /** @todo Change this to `Object.fromEntity()` in the future... */
   private toKeyValue(map: Map<string, any>): { [param: string]: any } | void {
      return map.size > 0
         ? Array.from(map).reduce(
              (obj, [key, value]) => Object.defineProperty(obj, key, { value, enumerable: true }),
              {}
           )
         : undefined;
   }

   /**
    * Call native GA Tag
    */
   gtag(...args: any[]) {
      try {
         this._gtag(...args.filter((x) => x !== undefined));
      } catch (err) {
         this.throw(err);
      }
   }

   /**
    * Send an event trigger to GA. It is the same as call:
    * ```js
    * gtag('event', 'video_auto_play_start', {
    *   'event_label': 'My promotional video',
    *   'event_category': 'video_auto_play'
    * });
    * ```
    *
    * @param action 'video_auto_play_start'
    * @param category 'video_auto_play'
    * @param label 'My promotional video'
    * @param value An value to measure something
    */
   event(action: GaActionEnum | string, paramsMap: Map<string, any> = new Map<string, any>()) {
      try {
         const params = this.toKeyValue(paramsMap);
         console.log("PARAMS FOR ANALYTICS MAP", params);
         if (params) {
            this.gtag("event", action as string, params);
         } else {
            this.gtag("event", action as string);
         }
      } catch (error) {
         this.throw(error);
      }
   }

   /**
    * Send an page view event. This is the same as
    *
    * ```js
    * gtag('config', 'GA_TRACKING_ID', {
    *   'page_title' : 'Homepage',
    *   'page_path': '/home'
    * });
    * ```
    *
    * The tracking ID is injected automatically by Inject Token NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN
    *
    * @param path /home
    * @param title Homepage
    * @param location '{ page_location }'
    * @param options '{ ... custom dimentions }'
    */
   pageView(path: string, title?: string, location?: string, options?: Object) {
      try {
         const opt = new Map<string, any>([["page_path", path]]);
         if (title) {
            opt.set("page_title", title);
         }
         if (location || this.document) {
            opt.set("page_location", location || this.document.location.href);
         }
         if (options) {
            Object.entries(options).map(([key, value]) => opt.set(key, value));
         }
         this.gtag("config", this.settings.trackingCode, this.toKeyValue(opt));
      } catch (error) {
         this.throw(error);
      }
   }

   /**
    * Defines persistent values on GoogleAnalytics
    *
    * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/config#user_id
    *
    * ```js
    * gtag('set', 'user_id', 'id');
    * ```
    */
   set(...options: Array<any>) {
      try {
         this._gtag("set", ...options);
      } catch (err) {
         this.throw(err);
      }
   }

   /**
    * Send an event to GA to report an application error. It is the same as
    *
    * ```js
    * gtag('event', 'exception', {
    *   'description': 'error_description',
    *   'fatal': false   // set to true if the error is fatal
    * });
    * ```
    *
    * @param description 'error_description'
    * @param fatal set to true if the error is fatal
    */
   exception(description?: string, fatal?: boolean) {
      try {
         const opt = new Map<string, any>();
         if (description) {
            opt.set("description", description);
         }
         if (fatal) {
            opt.set("fatal", fatal);
         }
         const params = this.toKeyValue(opt);
         if (params) {
            this.gtag("event", "exception", this.toKeyValue(opt));
         } else {
            this.gtag("event", "exception");
         }
      } catch (error) {
         this.throw(error);
      }
   }
}
